import { DATE_START_KEY, DATE_START_LABEL, DATE_STOP_KEY } from "../constant";
import { PREFERENCE_GROUP_URL, getFetchRequest, postFetchRequest } from "../store/requests";
import { v4 as uuid } from 'uuid';
import {errorCallback, makeUrl, successCallbackEmpty} from "../util";
import { preferencesGroupsStorage } from "./preferencesGroupsStorage";

export const transformLabelTypeToKey = (labelType) => {
    return labelType === DATE_START_LABEL ? DATE_START_KEY : DATE_STOP_KEY;
};

const createPreferenceGroupName = () => {
    return uuid().slice(0, 4) + uuid().slice(0, 4);
};

const needToAddValue = (group, index) => {
    return group.activityPeriods.length - 1 === index && 
        group.activityPeriods[index] &&
        group.activityPeriods[index].dateStart && 
        group.activityPeriods[index].dateStop;
};

const prepareActivityPeriods = (periodsList) => {
    const listOfPeriods = [];
    if(periodsList && periodsList.length) {
        for(let period of periodsList) {
            const splittedDateStart = period.dateStart.split('.');
            const splittedDateStop = period.dateStop.split('.');
            if (period.dateStart && period.dateStop) {
                const year = new Date().getFullYear();
                const startDay = String(splittedDateStart[0]).padStart(2, '0');
                const startMonth = String(splittedDateStart[1]).padStart(2, '0');
                const stopDay = String(splittedDateStop[0]).padStart(2, '0');
                const stopMonth = String(splittedDateStop[1]).padStart(2, '0');
                // Civil local dates (not UTC instants): 25.11 → 25 November 00:00 for any user TZ.
                listOfPeriods.push({
                    dateStart: `${year}-${startMonth}-${startDay}T00:00:00`,
                    dateStop: `${year}-${stopMonth}-${stopDay}T00:00:00`,
                });
            }
        }
    }
    return listOfPeriods;
};

const transformActivityPeriods = (periodsList) => {
    const listOfPeriods = [];
    if(periodsList && periodsList.length) {
        for(let period of periodsList) {
            const start = String(period.dateStart).slice(0, 10).split('-');
            const stop = String(period.dateStop).slice(0, 10).split('-');
            listOfPeriods.push({
                dateStart: `${start[2]}.${start[1]}.`,
                dateStop: `${stop[2]}.${stop[1]}.`,
            });
        }
    }
    return listOfPeriods;
};

export const preferenceGroupSaveService = {
    currentGroup: {isPermanent: true, name: createPreferenceGroupName(), isAutoAssigned: false, names: []},
    observersList: [],
    preferenceGroupId: '',
    lastGroupUpdateTime: null,
    loadPreferenceGroup() {
        try {
            if(this.preferenceGroupId) {
                getFetchRequest(makeUrl([PREFERENCE_GROUP_URL, this.preferenceGroupId, '/']), (response) => {
                    this.currentGroup = {
                        ...response,
                        activityPeriods: transformActivityPeriods(response.activityPeriods),
                        names: response.names || [],
                    };
                    this.notifyPreferenceGroupUpdateObservers();
                }, errorCallback);
            }
        } catch(err) {
            console.log(err);
        }
    },
    updatePreferenceGroupName(locale, value) {
        try {
            postFetchRequest(makeUrl([PREFERENCE_GROUP_URL, this.currentGroup.id, '/name/']),
                {
                    localizedName: value,
                    locale: locale,
                },
                () => {
                    if (!this.currentGroup.names) {
                        this.currentGroup.names = [];
                    }
                    const existing = this.currentGroup.names.find((it) => it.locale === locale);
                    if (existing) {
                        existing.name = value;
                    } else {
                        this.currentGroup.names.push({
                            locale: locale,
                            name: value,
                        });
                    }
                    const storedGroup = preferencesGroupsStorage.getById(this.currentGroup.id);
                    if (storedGroup) {
                        storedGroup.names = [...this.currentGroup.names];
                    }
                    this.lastGroupUpdateTime = Date.now();
                    this.notifyPreferenceGroupUpdateObservers();
                }, errorCallback, true);
        } catch(err) {
            console.log(err);
        }
    },
    updateItemLocalization(locale, value) {
        this.updatePreferenceGroupName(locale, value);
    },
    updatePreferenceGroupAutoAssigned(value) {
        this.currentGroup.isAutoAssigned = value;
        this.notifyPreferenceGroupUpdateObservers();
    },
    getPreferenceGroup() {
        return this.currentGroup;
    },
    updatePreferenceGroupActivityType(isPermanent) {
        this.currentGroup.isPermanent = isPermanent;
        if(!isPermanent) {
            this.currentGroup.activityPeriods = null;
            this.updatePreferenceGroupActivityPeriod();
        }
        this.notifyPreferenceGroupUpdateObservers();
    },
    updatePreferenceGroupActivityPeriod(activityPeriodKey, activityPeriodValue, currentPeriodIndex) {
        if(!this.currentGroup.activityPeriods) {
            this.currentGroup.activityPeriods = [{dateStart: '', dateStop: ''}];
        } else {
            this.currentGroup.activityPeriods[currentPeriodIndex] = {...this.currentGroup.activityPeriods[currentPeriodIndex], 
                [transformLabelTypeToKey(activityPeriodKey)]: activityPeriodValue};
        }
        if(needToAddValue(this.currentGroup, currentPeriodIndex)) {
            this.currentGroup.activityPeriods.push({dateStart: '', dateStop: ''});
        }
        this.notifyPreferenceGroupUpdateObservers();
    },
    registerPreferenceGroupUpdateObserver(observer) {
        this.observersList.push(observer);
    },
    unRegisterPreferenceGroupUpdateObserver(observer) {
        this.observersList = this.observersList.filter(observerFunction => observerFunction !== observer);
    },
    notifyPreferenceGroupUpdateObservers() {
        this.observersList.forEach(observer => observer());
    },
    deleteActivityPeriod(periodIndex) {
        this.currentGroup.activityPeriods = this.currentGroup.activityPeriods.filter((it, index) => index !== periodIndex);
        this.notifyPreferenceGroupUpdateObservers();
    },
    savePreferenceGroup() {
        const preparedBody = {
            isAutoAssigned: this.currentGroup.isAutoAssigned,
            isPermanent: this.currentGroup.isPermanent,
            activityPeriods: prepareActivityPeriods(this.currentGroup.activityPeriods),
            name: this.currentGroup.name,
        };
        if(this.preferenceGroupId) {
            postFetchRequest(makeUrl([PREFERENCE_GROUP_URL, this.preferenceGroupId, '/']), preparedBody, successCallbackEmpty, errorCallback, true);
        } else {
            postFetchRequest(makeUrl([PREFERENCE_GROUP_URL]), preparedBody, successCallbackEmpty, errorCallback, true);
        }
    },
    clearService() {
        this.currentGroup = {isPermanent: true, name: createPreferenceGroupName(), isAutoAssigned: false, names: []};
    },
};

export const initializePreferenceGroupSaveService = (preferenceGroupId) => {
    preferenceGroupSaveService.preferenceGroupId = preferenceGroupId;
    preferenceGroupSaveService.loadPreferenceGroup();
};
